import { createContext, useState, useEffect } from "react";
import { auth, db } from "../services/FirebaseConfig";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  sendPasswordResetEmail,
  confirmPasswordReset,
} from "firebase/auth";
import { getDoc, doc, setDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

const AuthContext = createContext();

const AuthContextProvider = ({ children }) => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [errorMessage, setErrorMessage] = useState({});
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * It's a useEffect hook that is listening to the onAuthStateChanged listener.
   * This function check if the currentUser exists to set it in the context and manage isAuthenticated state
   *
   */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        getInformationFromUserInDB(currentUser.uid)
          .then((firebaseUser) => {
            setUser({
              email: currentUser.email,
              uid: currentUser.uid,
              name: firebaseUser.name,
              lastName: firebaseUser.lastName,
            });
          })
          .catch((error) => {
            alert(
              `Ha ocurrido un error: ${error?.message}. Código ${error?.code}`
            );
          });
      }
      setIsLoading(false);
      setIsAuthenticated(!!currentUser);
    });
    return unsubscribe;
    // unsubscribe from onAuthStateChanged listener when component unload
  }, []);

  /**
   * It gets the information of a user from the database.
   * @returns The data from the user in the database.
   */
  const getInformationFromUserInDB = async (uid) => {
    const documentReference = doc(db, "compradores", uid);
    const encryptedDoc = await getDoc(documentReference);
    const data = encryptedDoc.data();
    return data;
  };

  /**
   * This function creates a user with an email and password,
   * then saves that user to Firestore without their password
   */
  const registerUser = async (userData) => {
    createUserWithEmailAndPassword(auth, userData.email, userData.password)
      .then(async ({ user: firebaseUser }) => {
        // Create user in Firestore
        const { password, password2, ...userWithoutPassword } = userData;
        const userToCreate = doc(db, "compradores", firebaseUser.uid);
        await setDoc(userToCreate, {
          ...userWithoutPassword,
          id: firebaseUser.uid,
        });
        setErrorMessage({});
      })
      .catch((err) => {
        switch (err.code) {
          case "auth/email-already-in-use":
            setErrorMessage({ email: "El correo electrónico ya existe" });
            break;
          case "auth/invalid-email":
            setErrorMessage({ email: "El correo electrónico no es valido" });
            break;
          case "auth/weak-password":
            setErrorMessage({
              password: "La contraseña no es segura",
            });
            break;
          default:
            alert(`Ha ocurrido un error: ${err?.message}. Código ${err?.code}`);
        }
      });
  };

  /**
   * This function takes an user and login them with their email and password
   */
  const logUser = async (userData) => {
    signInWithEmailAndPassword(auth, userData.email, userData.password)
      .then(() => {
        setErrorMessage({});
      })
      .catch((err) => {
        switch (err.code) {
          case "auth/invalid-email":
            setErrorMessage({ email: "El correo no es válido" });
            break;
          case "auth/user-not-found":
            setErrorMessage({ email: "El usuario no fué encontrado" });
            break;
          case "auth/wrong-password":
            setErrorMessage({ password: "La contraseña es incorrecta" });
            break;
          case "auth/internal-error":
            setErrorMessage({ password: "La contraseña es requerida" });
            break;
          default:
            setErrorMessage({
              password: "Error inesperado. Por favor, recarga la página",
            });
        }
      });
  };

  /**
   * This function calls the signOut function and sets the user
   * to null.
   */
  const logOut = async () => {
    await signOut(auth);
    setUser(null);
  };

  /**
   * This function takes an email and then, sends a password reset email with
   * successful message, and finally, navigate to home page
   */
  const forgotPassword = (email) => {
    sendPasswordResetEmail(auth, email)
      .then(() => {
        setErrorMessage({});
        navigate("/");
        alert(
          "Se ha enviado un correo electrónico con el enlace para restaurar tu contraseña"
        );
      })
      .catch((err) => {
        switch (err.code) {
          case "auth/invalid-email":
            setErrorMessage({ email: "El correo no es válido" });
            break;
          case "auth/user-not-found":
            setErrorMessage({ email: "El usuario no fué encontrado" });
            break;
          case "auth/internal-error":
            setErrorMessage({ email: "El correo es requerido" });
            break;
          default:
            setErrorMessage({
              email: "Error inesperado. Por favor, recarga la página",
            });
        }
      });
  };

  /**
   * This function checks if the oobCode is not null, then,
   * confirm the password reset with a successful message, and finally,
   * navigate to the login page
   */
  const resetPassword = (oobCode, newPassword) => {
    if (!oobCode) {
      setErrorMessage({
        password2: "El código de restauración no es valido o ha expirado",
      });
      return;
    }
    confirmPasswordReset(auth, oobCode, newPassword)
      .then(() => {
        setErrorMessage({});
        navigate("/login");
        alert("Tu contraseña ha sido restablecida correctamente");
      })
      .catch((err) => {
        switch (err.code) {
          case "auth/internal-error":
            setErrorMessage({ password: "La contraseña es requerida" });
            break;
          case "auth/invalid-action-code":
            setErrorMessage({
              password2: "El código de restauración no es valido o ha expirado",
            });
            break;
          default:
            setErrorMessage({
              password: "Error inesperado. Por favor, recarga la página",
            });
        }
      });
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        setIsAuthenticated,
        registerUser,
        logUser,
        logOut,
        forgotPassword,
        resetPassword,
        user,
        errorMessage,
      }}
    >
      {isLoading === false && children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthContextProvider };
